import {
  Button,
  Flex,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Span,
} from "@ir/ui";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";

export type MasterDataDeductionTypeDeleteMode = "selected" | "all";

interface MasterDataDeductionTypeDeleteToolbarProps {
  selectedCount: number;
  onDeleteSelected: () => void;
  onDeleteAll: () => void;
  className?: string;
}

export function MasterDataDeductionTypeDeleteToolbar({
  selectedCount,
  onDeleteSelected,
  onDeleteAll,
  className,
}: Readonly<MasterDataDeductionTypeDeleteToolbarProps>) {
  return (
    <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_DELETE]}>
      {selectedCount > 0 ? (
        <Button
          size="small"
          color="error"
          variant="outline"
          onPress={onDeleteSelected}
          className={className}
        >
          <Icon slot="start" name={Icons.Trash} />
          Delete Selected
        </Button>
      ) : (
        <Button
          size="small"
          color="error"
          variant="outline"
          onPress={onDeleteAll}
          className={className}
        >
          <Icon slot="start" name={Icons.Trash} />
          Delete All
        </Button>
      )}
    </HasAnyPermission>
  );
}

interface MasterDataDeductionTypeDeleteConfirmModalProps {
  deleteMode: MasterDataDeductionTypeDeleteMode | null;
  isDeleting: boolean;
  selectedCount: number;
  allTitle: string;
  selectedTitle: string;
  allDescription: string;
  entitySingular: string;
  entityPlural: string;
  onClose: () => void;
  onConfirm: () => void;
}

export function MasterDataDeductionTypeDeleteConfirmModal({
  deleteMode,
  isDeleting,
  selectedCount,
  allTitle,
  selectedTitle,
  allDescription,
  entitySingular,
  entityPlural,
  onClose,
  onConfirm,
}: Readonly<MasterDataDeductionTypeDeleteConfirmModalProps>) {
  return (
    <Modal
      isOpen={deleteMode != null}
      onClose={() => {
        if (!isDeleting) onClose();
      }}
      size="small"
      closable={!isDeleting}
      headerContent={
        <Heading level={6} className="text-grey-700 font-bold m-0">
          {deleteMode === "all" ? allTitle : selectedTitle}
        </Heading>
      }
      footerContent={
        <Flex gap={0.5} className="flex-1" justifyContent="space-between">
          <Button
            size="small"
            color="grey"
            isDisabled={isDeleting}
            onPress={onClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            isLoading={isDeleting}
            isDisabled={isDeleting}
            onPress={onConfirm}
          >
            {deleteMode === "all" ? "Delete all" : "Delete Selected"}
          </Button>
        </Flex>
      }
      classNames={{ body: "p-0" }}
    >
      <Paragraph className="m-0 py-4 px-3">
        {deleteMode === "all" ? (
          allDescription
        ) : (
          <>
            Are you sure you want to delete{" "}
            <Span className="font-bold text-primary-500">{selectedCount}</Span>
            <Span className="ps-1">
              {selectedCount === 1 ? entitySingular : entityPlural}
            </Span>
            ?
          </>
        )}
      </Paragraph>
    </Modal>
  );
}
